import { useEffect, useState } from "react";

import { isEmpty, map, partition } from "lodash/fp";

import { Stack, Typography } from "@mui/material";

import SettingAlert from "@app/analytics/account/components/SettingAlert";
import { TeamPermissionsTableRow } from "@app/analytics/account/types";
import BasicModal, { ModalProps } from "@app/analytics/components/BasicModal";
import Table from "@app/analytics/components/Table/Table";
import TableBody from "@app/analytics/components/Table/TableBody";
import TableHead from "@app/analytics/components/Table/TableHead";
import TableRow from "@app/analytics/components/Table/TableRow";
import UpsellTooltipWrapper from "@app/analytics/components/UpsellTooltipWrapper";
import useDataTable, { HeadCell } from "@app/common/hooks/useDataTable";
import {
  EnterpriseRole,
  EnterpriseWorkspaceTeam,
} from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { UpdateTeamUserBody } from "@app/common/workspaces/WorkspaceContext";

import TeamMenuCell from "./TeamMenuCell";
import TeamNameCell from "./TeamNameCell";
import RoleCell from "../../components/RoleCell";
import WorkspaceActionButton from "../../components/WorkspaceActionButton";

const TEAM_ROLE_OPTIONS: EnterpriseRole[] = ["manager", "member"];

const HEAD_CELLS: HeadCell<TeamPermissionsTableRow>[] = [
  {
    id: "name",
    label: "Team Name",
    sortable: true,
    sortBy: (value) => (value ? 1 : -1),
  },
  {
    id: "role",
    label: "Team Role",
    sortable: true,
  },
  {
    id: "menu",
    label: "",
    sortable: false,
  },
];

const NEW_TEAM_ROW: TeamPermissionsTableRow = {
  role: "member",
};

interface TeamPermissionsModalProps extends ModalProps {
  userId: string | null;
}

const toUpdateTeamUserBody = (
  row: TeamPermissionsTableRow,
  userId: string
): UpdateTeamUserBody => ({
  teamId: row.teamId!,
  userId,
  role: row.role,
});

const makeTableData = (
  teams: EnterpriseWorkspaceTeam[],
  userTeams: EnterpriseWorkspaceTeam[]
): TeamPermissionsTableRow[] =>
  userTeams.map((team) => ({
    teamId: team.id,
    name: team.name,
    role: team.users[0].role, // this user will be the only user in the users list in this context
  }));

const TeamPermissionsModal = ({
  onClose,
  open,
  userId,
}: TeamPermissionsModalProps) => {
  const {
    addOrUpdateTeamUsers,
    removeUsersFromTeam,
    workspace,
    workspaceIsInactive,
  } = useWorkspace();

  const user = workspace.users.find((u) => u.userId === userId);
  const workspaceTeams = workspace.teams ?? [];
  const userTeams = user?.teams ?? [];

  const [loading, setLoading] = useState<boolean>(false);
  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);
  const [tableData, setTableData] = useState<TeamPermissionsTableRow[]>([]);

  const existingUserTeamIds = map("id", userTeams);

  useEffect(() => {
    setTableData(makeTableData(workspaceTeams, userTeams));
  }, [userTeams.length]);

  const handleSaveChangesSuccess = (success: boolean) => {
    setLoading(false);
    if (success) {
      onClose();
    } else {
      setShowErrorAlert(true);
    }
  };

  const handleSaveChanges = () => {
    setLoading(true);

    const rows = tableData.filter(
      (row) => Boolean(row.teamId) && Boolean(row.role)
    );

    const [rowsToUpdate, rowsToAdd] = partition(
      (row) => existingUserTeamIds.includes(row.teamId!),
      rows
    );

    addOrUpdateTeamUsers(
      {
        usersToCreate: rowsToAdd.map((row) =>
          toUpdateTeamUserBody(row, user!.userId)
        ),
        usersToUpdate: rowsToUpdate.map((row) =>
          toUpdateTeamUserBody(row, user!.userId)
        ),
      },
      { onSuccess: handleSaveChangesSuccess }
    );
  };

  const { handleSort, order, orderBy, sortedData } = useDataTable(
    tableData,
    HEAD_CELLS,
    { defaultOrder: "asc" }
  );

  const handleRoleChange = (teamId?: string) => (newRole: EnterpriseRole) => {
    if (!teamId) return;

    setTableData((currentData) =>
      currentData.map((row) =>
        row.teamId === teamId ? { ...row, role: newRole } : row
      )
    );
  };

  const handleTeamSelect = (index: number) => (teamId: string) => {
    const team = workspaceTeams.find((t) => t.id === teamId);
    setTableData((currentData) =>
      currentData.map((row, i) =>
        index === i ? { ...row, name: team!.name, teamId } : row
      )
    );
  };

  const handleAddTeamRow = () => {
    setTableData([...tableData, NEW_TEAM_ROW]);
  };

  const handleRemoveFromTeam = (teamId?: string) => () => {
    if (!teamId) return;

    const canShallowRemove = !existingUserTeamIds.includes(teamId);

    if (canShallowRemove) {
      setTableData((currentData) =>
        currentData.filter((row) => row.teamId !== teamId)
      );
    } else {
      removeUsersFromTeam.mutate({ teamId, userIds: [user!.userId] });
    }
  };

  const teamsUserIsIn = map("name", tableData);
  const selectableTeams = workspaceTeams.filter(
    (team) => !teamsUserIsIn.includes(team.name)
  );

  const showAddTeamsButton = tableData.length < workspaceTeams.length;

  // default to allowing the user to set a new team instead of showing an empty table
  if (isEmpty(sortedData)) {
    handleAddTeamRow();
  }

  return (
    <BasicModal
      loading={loading}
      onSubmit={handleSaveChanges}
      submitText="Save Changes"
      title="Team Permissions"
      onClose={onClose}
      open={open}
      sx={{ minWidth: "500px" }}
    >
      <SettingAlert
        severity="error"
        show={showErrorAlert}
        onHide={() => setShowErrorAlert(false)}
      >
        Some changes failed to save.
      </SettingAlert>
      <Typography
        variant="bodyPrimaryMed"
        sx={{ color: "shades.neutral.700", mt: 1 }}
      >
        {user?.fullName}
      </Typography>
      <Table sx={{ my: 2 }}>
        <TableHead
          headCells={HEAD_CELLS}
          onSort={handleSort}
          order={order}
          orderBy={orderBy}
        />
        <TableBody>
          {sortedData.map((row, i) => {
            const noTeamSelected = !row.name;
            const isInTeam =
              !!row.teamId && existingUserTeamIds.includes(row.teamId);
            return (
              <TableRow key={`row-${row.teamId}-${i}`}>
                <TeamNameCell
                  onTeamSelect={handleTeamSelect(i)}
                  team={row}
                  teams={selectableTeams}
                />
                <RoleCell
                  disabled={noTeamSelected}
                  onRoleChange={handleRoleChange(row.teamId)}
                  options={TEAM_ROLE_OPTIONS}
                  role={row.role}
                />
                <TeamMenuCell
                  disabled={noTeamSelected}
                  onRemove={handleRemoveFromTeam(row.teamId)}
                  showConfirmation={isInTeam}
                />
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {showAddTeamsButton && (
        <Stack direction="row">
          <UpsellTooltipWrapper show={workspaceIsInactive}>
            <WorkspaceActionButton
              disabled={workspaceIsInactive}
              onClick={handleAddTeamRow}
              text="Add Team"
            />
          </UpsellTooltipWrapper>
        </Stack>
      )}
    </BasicModal>
  );
};

export default TeamPermissionsModal;
